import React, { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import Card from "../../components/Card";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Image from "../../components/Image";
import imageOfficeWorking from "../../images/office-working.png";
import { recordSignupTermination } from "../../utils/flowActivityUtils";
import { getCountryName } from "../../utils/countryRiskUtils";

// Maps the flow's transactionType to the noun shown in the card title.
// "BuyingAndSelling" is treated as a sale, matching the pre-existing
// isSelling() routing in SignupFlow.
const TRANSACTION_LABELS = {
  Buying: "purchase",
  Selling: "sale",
  BuyingAndSelling: "sale",
  Remortgaging: "remortgage",
};

// client_signup_requests.transaction_type is nullable, and some staff-app
// producers do not set it. An absent value is normal: it gets the "signup"
// title and no report. An unrecognised value means webapp-alpha gained a fifth
// transaction type and TRANSACTION_LABELS did not keep up.
const isExpectedTransactionType = (transactionType) => transactionType == null || !!TRANSACTION_LABELS[transactionType];

const HighRiskCountrySorryPage = ({ flowId, countryCode, transactionType }) => {
  const hasInitiated = useRef(false);
  const countryName = getCountryName(countryCode);
  const transactionLabel = TRANSACTION_LABELS[transactionType] ?? "signup";

  // Deps are kept honest for lint; the ref guard prevents re-fire on any prop
  // change (and also absorbs StrictMode's double-effect), so this effect runs
  // exactly once on first mount.
  //
  // flowId is optional — only the lead flows (Signup, RemoSignup) have a
  // FlowActivity row to update; the joint-client flow doesn't, so callers there
  // omit flowId and we skip the UPDATE-by-flowId-no-op rather than send a
  // useless request.
  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;
    if (!isExpectedTransactionType(transactionType) || !countryName) {
      Sentry.captureMessage("HighRiskCountrySorryPage rendered with unexpected props", {
        tags: { component: "HighRiskCountrySorryPage" },
        extra: { transactionType, countryCode },
      });
    }
    if (flowId != null) {
      recordSignupTermination(flowId, `client lives in country: ${countryName ?? "unknown"}`);
    }
  }, [flowId, countryName, countryCode, transactionType]);

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title={`We’re not able to help with this ${transactionLabel}`} removeTopPadding>
        {countryName ? (
          <p>We are not able to represent people who live in {countryName}.</p>
        ) : (
          <p>We are not able to take you on as a client.</p>
        )}
        <p>Sorry we are not able to help. We wish you all the best with your move.</p>
      </Card>
      <Image src={imageOfficeWorking} fullWidth />
    </React.Fragment>
  );
};

export default HighRiskCountrySorryPage;
